import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'

const PROVIDER_CONFIG: Record<string, {
  label: string; icon: string; colour: string; regions: string
}> = {
  stripe:      { label: 'Stripe',      icon: '💳', colour: '#635BFF', regions: 'Global' },
  paypal:      { label: 'PayPal',      icon: '🅿',  colour: '#003087', regions: 'Global' },
  flutterwave: { label: 'Flutterwave', icon: '⚡',  colour: '#F5A623', regions: 'West, East & Southern Africa' },
  paystack:    { label: 'Paystack',    icon: '🔵',  colour: '#00C3F7', regions: 'Nigeria, Ghana, Kenya, South Africa' },
  mpesa:       { label: 'M-Pesa',      icon: '📱',  colour: '#00A550', regions: 'Kenya, Tanzania, Mozambique, Rwanda, Uganda' },
}

const PLANS = ['trial', 'individual', 'starter', 'professional', 'enterprise'] as const

function PlanBadge({ plan }: { plan: string }) {
  const PLANS_MAP: Record<string, { label: string; bg: string; text: string }> = {
    trial:        { label: 'Trial',        bg: '#F7F5F0', text: '#6B6760' },
    individual:   { label: 'Individual',   bg: '#E0FAF4', text: '#0E7A6A' },
    starter:      { label: 'Starter',      bg: '#EBF4FF', text: '#1E4E8C' },
    professional: { label: 'Professional', bg: '#E1F5EE', text: '#0F6E56' },
    enterprise:   { label: 'Enterprise',   bg: '#F0E4B8', text: '#7A5C00' },
  }
  const p = PLANS_MAP[plan] ?? PLANS_MAP.trial
  return (
    <span
      className="text-[10px] font-medium px-2 py-0.5 rounded-full"
      style={{ background: p.bg, color: p.text }}
    >
      {p.label}
    </span>
  )
}

function StatusBadge({ status }: { status: string }) {
  const S: Record<string, { label: string; bg: string; text: string }> = {
    active:    { label: 'Active',    bg: '#E1F5EE', text: '#0F6E56' },
    past_due:  { label: 'Past Due',  bg: '#FAEEDA', text: '#854F0B' },
    cancelled: { label: 'Cancelled', bg: '#FEE8E8', text: '#A32D2D' },
    suspended: { label: 'Suspended', bg: '#2C2C2A', text: '#FFFFFF' },
  }
  const s = S[status] ?? S.active
  return (
    <span
      className="text-[10px] font-medium px-2 py-0.5 rounded-full"
      style={{ background: s.bg, color: s.text }}
    >
      {s.label}
    </span>
  )
}

export function BillingView() {
  const { toast } = useToast()
  const qc = useQueryClient()
  const [editSub, setEditSub] = useState<any | null>(null)
  const [newPlan, setNewPlan] = useState('')
  const [saving, setSaving] = useState(false)

  const { data: subscriptions } = useQuery({
    queryKey: ['org-subscriptions'],
    queryFn: async () => {
      const { data } = await (supabase
        .from('org_subscriptions' as any)
        .select('*, organizations(name, id)')
        .order('created_at', { ascending: false }) as any)
      return (data ?? []) as any[]
    },
  })

  const { data: providers } = useQuery({
    queryKey: ['payment-providers'],
    queryFn: async () => {
      const { data } = await (supabase
        .from('payment_provider_settings' as any)
        .select('*')
        .order('provider') as any)
      return (data ?? []) as any[]
    },
  })

  const { data: transactions } = useQuery({
    queryKey: ['recent-transactions'],
    queryFn: async () => {
      const { data } = await (supabase
        .from('payment_transactions' as any)
        .select('*, organizations(name)')
        .order('created_at', { ascending: false })
        .limit(20) as any)
      return (data ?? []) as any[]
    },
  })

  const toggleProviderMutation = useMutation({
    mutationFn: async ({ provider, enabled }: { provider: string; enabled: boolean }) => {
      await (supabase
        .from('payment_provider_settings' as any)
        .update({ is_enabled: enabled })
        .eq('provider', provider) as any)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payment-providers'] })
    },
    onError: () => {
      toast({ title: 'Failed to update provider.', variant: 'destructive' })
    },
  })

  const handleEditPlan = async () => {
    if (!editSub || !newPlan) return
    setSaving(true)
    try {
      await (supabase
        .from('org_subscriptions' as any)
        .update({ plan: newPlan })
        .eq('id', editSub.id) as any)
      toast({ title: 'Plan updated.' })
      qc.invalidateQueries({ queryKey: ['org-subscriptions'] })
      setEditSub(null)
    } catch {
      toast({ title: 'Failed to update plan.', variant: 'destructive' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6 p-5">

      {/* Header */}
      <div>
        <h2 className="text-[17px] font-semibold text-[#0D2240] dark:text-white">
          Billing Management
        </h2>
        <p className="text-[12px] text-muted-foreground mt-0.5 text-justify">
          Manage subscription plans for all client organisations and configure accepted payment providers.
        </p>
      </div>

      {/* Payment providers */}
      <section>
        <h3 className="text-[12px] font-semibold uppercase tracking-wide text-muted-foreground mb-3">
          Payment Providers
        </h3>
        {providers.length === 0 ? (
          <div className="text-[12px] text-muted-foreground bg-muted/30 rounded-xl p-4">
            No payment provider settings found. Run the database migration to seed provider records.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {providers.map((provider: any) => {
              const cfg = PROVIDER_CONFIG[provider.provider]
              if (!cfg) return null
              return (
                <div key={provider.provider} className="bg-background border border-border rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{cfg.icon}</span>
                      <span className="text-[13px] font-medium">{cfg.label}</span>
                    </div>
                    <button
                      onClick={() =>
                        toggleProviderMutation.mutate({
                          provider: provider.provider,
                          enabled: !provider.is_enabled,
                        })
                      }
                      className={`w-10 h-5 rounded-full transition-colors relative flex items-center ${
                        provider.is_enabled ? 'bg-[#006B3C]' : 'bg-[#DDD8CE]'
                      }`}
                    >
                      <span
                        className={`absolute w-4 h-4 rounded-full bg-white shadow transition-transform ${
                          provider.is_enabled ? 'translate-x-5' : 'translate-x-0.5'
                        }`}
                      />
                    </button>
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {(provider.currencies ?? []).join(', ')}
                  </div>
                  <div className="text-[10.5px] text-muted-foreground mt-0.5">{cfg.regions}</div>
                  {provider.is_enabled && (
                    <div className="mt-2 text-[10px] font-medium text-[#006B3C] flex items-center gap-1">
                      ✓ Active
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
        <p className="text-[11px] text-muted-foreground mt-2 text-justify">
          Enabling a payment provider requires configuring its API credentials in Supabase Edge Function
          secrets. Contact your developer to configure.
        </p>
      </section>

      {/* Organisation subscriptions */}
      <section>
        <h3 className="text-[12px] font-semibold uppercase tracking-wide text-muted-foreground mb-3">
          Organisation Subscriptions
        </h3>
        <div className="bg-background border border-border rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-muted border-b border-border">
                {['Organisation', 'Plan', 'Status', 'Provider', 'Currency', 'Next Payment', 'Actions'].map(h => (
                  <th key={h} className="text-left px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(subscriptions ?? []).map((sub: any) => (
                <tr key={sub.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3 text-[13px] font-medium">
                    {sub.organizations?.name ?? ''}
                  </td>
                  <td className="px-4 py-3">
                    <PlanBadge plan={sub.plan} />
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={sub.status} />
                  </td>
                  <td className="px-4 py-3 text-[12px] text-muted-foreground">
                    {PROVIDER_CONFIG[sub.payment_provider]?.label ?? 'Manual'}
                  </td>
                  <td className="px-4 py-3 text-[12px] text-muted-foreground">
                    {sub.currency ?? 'USD'}
                  </td>
                  <td className="px-4 py-3 text-[12px] text-muted-foreground">
                    {sub.next_payment_date
                      ? new Date(sub.next_payment_date).toLocaleDateString('en', {
                          day: 'numeric', month: 'short', year: 'numeric',
                        })
                      : ''}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => { setEditSub(sub); setNewPlan(sub.plan) }}
                      className="text-[11.5px] text-[#006B3C] hover:underline font-medium"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {(subscriptions ?? []).length === 0 && (
            <div className="py-10 text-center text-[12px] text-muted-foreground">
              No subscriptions yet.
            </div>
          )}
        </div>
      </section>

      {/* Recent transactions */}
      <section>
        <h3 className="text-[12px] font-semibold uppercase tracking-wide text-muted-foreground mb-3">
          Recent Transactions
        </h3>
        <div className="bg-background border border-border rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-muted border-b border-border">
                {['Date', 'Organisation', 'Amount', 'Currency', 'Provider', 'Status'].map(h => (
                  <th key={h} className="text-left px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(transactions ?? []).map((tx: any) => (
                <tr key={tx.id} className="border-b border-border hover:bg-muted/30">
                  <td className="px-4 py-3 text-[12px] text-muted-foreground">
                    {new Date(tx.created_at).toLocaleDateString('en', {
                      day: 'numeric', month: 'short', year: 'numeric',
                    })}
                  </td>
                  <td className="px-4 py-3 text-[12.5px] font-medium">
                    {tx.organizations?.name ?? ''}
                  </td>
                  <td className="px-4 py-3 text-[12.5px]">
                    {tx.amount_cents != null
                      ? (tx.amount_cents / 100).toFixed(2)
                      : ''}
                  </td>
                  <td className="px-4 py-3 text-[12px] text-muted-foreground">
                    {tx.currency ?? ''}
                  </td>
                  <td className="px-4 py-3 text-[12px]">
                    {PROVIDER_CONFIG[tx.payment_provider]?.icon}{' '}
                    {PROVIDER_CONFIG[tx.payment_provider]?.label ?? tx.payment_provider ?? ''}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                        tx.status === 'completed'
                          ? 'bg-[#E1F5EE] text-[#0F6E56]'
                          : tx.status === 'failed'
                          ? 'bg-[#FEE8E8] text-[#A32D2D]'
                          : 'bg-[#FAEEDA] text-[#854F0B]'
                      }`}
                    >
                      {tx.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {(transactions ?? []).length === 0 && (
            <div className="py-10 text-center text-[12px] text-muted-foreground">
              No transactions recorded yet.
            </div>
          )}
        </div>
      </section>

      {/* Edit plan dialog */}
      <Dialog open={!!editSub} onOpenChange={o => !o && setEditSub(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Edit Plan: {editSub?.organizations?.name}</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <Select value={newPlan} onValueChange={setNewPlan}>
              <SelectTrigger>
                <SelectValue placeholder="Select plan" />
              </SelectTrigger>
              <SelectContent>
                {PLANS.map(p => (
                  <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditSub(null)}>Cancel</Button>
            <Button onClick={handleEditPlan} disabled={saving || !newPlan}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
